import { useCallback, useState, type KeyboardEvent } from 'react';
import { Redo2, Undo2 } from 'lucide-react';
import {
  removeShapeVertex,
  roomShapeFromRoom,
  type Room,
  type RoomShape,
  type ShapePoint,
} from '@peles/shared';
import { EntityModal } from '../../common/EntityModal';
import { SegmentedControl } from '../../common/SegmentedControl';
import { useDialog } from '../../../context/DialogContext';
import { useUpdateRoom } from '../../../hooks/useFloorPlans';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { RoomShapeCanvas, type CanvasMode, type SnapSettings } from './RoomShapeCanvas';
import {
  FeaturesPanel,
  OpeningsPanel,
  PresetPanel,
  SelectionPanel,
  WallsPanel,
  inputClass,
} from './RoomShapePanels';
import { TracePanel } from './TracePanel';
import { useRoomShapeEditor } from './useRoomShapeEditor';
import { useTraceDrawing } from './useTraceDrawing';

type Tab = 'shape' | 'walls' | 'trace' | 'features' | 'doors';

const TABS: { id: Tab; label: string }[] = [
  { id: 'shape', label: 'Shape' },
  { id: 'walls', label: 'Walls' },
  { id: 'trace', label: 'Trace' },
  { id: 'features', label: 'Features' },
  { id: 'doors', label: 'Doors' },
];

const GRID_OPTIONS = [
  { id: '0', label: 'Free' },
  { id: '0.5', label: '6 in' },
  { id: '1', label: '1 ft' },
];

interface RoomShapeEditorModalProps {
  open: boolean;
  onClose: () => void;
  room: Room;
  onSaved?: (room: Room) => void;
}

/** The server's reasons a shape was refused, when it gave them. */
export function shapeProblemsFromError(err: unknown): string[] {
  const problems = (err as { response?: { data?: { error?: { problems?: unknown } } } })?.response
    ?.data?.error?.problems;
  return Array.isArray(problems) ? problems.filter((p): p is string => typeof p === 'string') : [];
}

/**
 * The ceiling height, kept as typed until it is a height a room can have —
 * committing each keystroke would clear "1" on the way to "12".
 */
export function CeilingInput({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (ft: number | undefined) => void;
}) {
  const [draft, setDraft] = useState(value?.toString() ?? '');
  return (
    <label className="ml-auto inline-flex items-center gap-1.5 text-sm text-gray-700">
      Ceiling
      <input
        className={`${inputClass} w-16`}
        type="number"
        min={4}
        max={200}
        aria-label="Ceiling height (ft)"
        placeholder="—"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          const n = parseFloat(e.target.value);
          if (e.target.value === '') onChange(undefined);
          else if (n >= 4 && n <= 200 && n !== value) onChange(n);
        }}
      />
      ft
    </label>
  );
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return !['range', 'checkbox', 'radio', 'button'].includes(type);
}

/**
 * Draw a room as it really is: start from a shape, type the walls, or trace
 * the venue's drawing — then adjust corners, curve walls, and add the pillars,
 * doors and windows. Saved on the room, so every plan in it starts right.
 */
export function RoomShapeEditorModal(props: RoomShapeEditorModalProps) {
  // Mounted only while open, so every opening starts from the stored shape.
  return props.open ? <RoomShapeEditor {...props} /> : null;
}

function RoomShapeEditor({ onClose, room, onSaved }: RoomShapeEditorModalProps) {
  const [initial] = useState<RoomShape>(() => roomShapeFromRoom(room));
  const editor = useRoomShapeEditor(initial);
  const { shape, measurements, problems } = editor;
  const drawing = useTraceDrawing(room.id, shape.background);
  const updateRoom = useUpdateRoom();
  const { confirm } = useDialog();

  const [tab, setTab] = useState<Tab>(() => (room.shape ? 'features' : 'shape'));
  const [mode, setMode] = useState<CanvasMode>('edit');
  const [snap, setSnap] = useState<SnapSettings>({ gridFt: 0.5, rightAngles: true });
  const [tracePoints, setTracePoints] = useState<ShapePoint[]>([]);
  const [calibrationPoints, setCalibrationPoints] = useState<ShapePoint[]>([]);
  const [serverProblems, setServerProblems] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  const dirty = shape !== initial;

  const changeTab = (t: Tab) => {
    setTab(t);
    // Tracing clicks mean nothing on the other tabs.
    if (t !== 'trace') {
      setMode('edit');
      setTracePoints([]);
      setCalibrationPoints([]);
    }
  };

  const finishTrace = useCallback(() => {
    if (tracePoints.length < 3) return;
    editor.replaceOutline(tracePoints.map((p) => ({ x: p.x, y: p.y })));
    setTracePoints([]);
    setMode('edit');
  }, [tracePoints, editor]);

  const requestClose = async () => {
    if (
      dirty &&
      !(await confirm({
        title: 'Discard changes?',
        message: 'The room shape has changes that are not saved.',
        confirmLabel: 'Discard',
        variant: 'danger',
      }))
    ) {
      return;
    }
    onClose();
  };

  const save = async () => {
    setServerProblems([]);
    setSaveError(null);
    try {
      const saved = await updateRoom.mutateAsync({ id: room.id, updates: { shape } });
      onSaved?.(saved);
      onClose();
    } catch (err) {
      const list = shapeProblemsFromError(err);
      setServerProblems(list);
      setSaveError(
        list.length ? null : extractApiErrorMessage(err, 'The room shape could not be saved.'),
      );
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (isTyping(e.target)) return;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) editor.redo();
      else editor.undo();
      return;
    }
    if (mod && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      editor.redo();
      return;
    }
    if (mode === 'trace') {
      if (e.key === 'Backspace') {
        e.preventDefault();
        setTracePoints((pts) => pts.slice(0, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        finishTrace();
      }
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      const sel = editor.selection;
      if (!sel) return;
      e.preventDefault();
      if (sel.type === 'feature') editor.removeFeature(sel.id);
      else if (sel.type === 'opening') editor.removeOpening(sel.id);
      else if (sel.type === 'vertex' && shape.outline.length > 3) {
        editor.update((s) => removeShapeVertex(s, sel.index));
        editor.setSelection(null);
      }
    }
  };

  const shownProblems = serverProblems.length ? serverProblems : problems;

  return (
    <EntityModal
      open
      onClose={() => void requestClose()}
      title={`Room shape — ${room.name}`}
      size="xl"
      closeOnBackdrop={false}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-gray-500" data-testid="room-measurements">
            {measurements.areaSqFt.toLocaleString()} sq ft
            {measurements.usableAreaSqFt !== measurements.areaSqFt &&
              ` · ${measurements.usableAreaSqFt.toLocaleString()} usable`}
            {` · ${measurements.widthFt} × ${measurements.heightFt} ft · ${measurements.walls} walls · ${measurements.perimeterFt} ft of wall`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => void requestClose()}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
              disabled={problems.length > 0 || updateRoom.isPending || mode !== 'edit'}
              onClick={() => void save()}
            >
              {updateRoom.isPending ? 'Saving…' : 'Save shape'}
            </button>
          </div>
        </div>
      }
    >
      <div
        className="flex flex-col gap-3 outline-none md:flex-row"
        onKeyDown={onKeyDown}
        tabIndex={-1}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-label="Undo"
              title="Undo (Ctrl+Z)"
              className="rounded-md border border-gray-300 p-1.5 text-gray-600 disabled:opacity-40"
              disabled={!editor.canUndo}
              onClick={editor.undo}
            >
              <Undo2 size={16} />
            </button>
            <button
              type="button"
              aria-label="Redo"
              title="Redo (Ctrl+Shift+Z)"
              className="rounded-md border border-gray-300 p-1.5 text-gray-600 disabled:opacity-40"
              disabled={!editor.canRedo}
              onClick={editor.redo}
            >
              <Redo2 size={16} />
            </button>
            <label className="ml-2 inline-flex items-center gap-1.5 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={snap.rightAngles}
                onChange={(e) => setSnap({ ...snap, rightAngles: e.target.checked })}
              />
              Right angles
            </label>
            <SegmentedControl
              ariaLabel="Snap to grid"
              options={GRID_OPTIONS}
              value={String(snap.gridFt)}
              onChange={(v) => setSnap({ ...snap, gridFt: Number(v) })}
            />
            <CeilingInput
              value={shape.wallHeightFt}
              onChange={(wallHeightFt) => editor.update((s) => ({ ...s, wallHeightFt }))}
            />
          </div>
          <div className="h-[55vh] min-h-[320px] overflow-hidden rounded-md border border-gray-200">
            <RoomShapeCanvas
              editor={editor}
              mode={mode}
              snap={snap}
              background={drawing.canvasBackground}
              tracePoints={tracePoints}
              onTracePoint={(p) => setTracePoints((pts) => [...pts, p])}
              onTraceClose={finishTrace}
              calibrationPoints={calibrationPoints}
              onCalibrationPoint={(p) =>
                setCalibrationPoints((pts) => (pts.length >= 2 ? [p] : [...pts, p]))
              }
            />
          </div>
          {shownProblems.length > 0 && (
            <ul
              className="space-y-0.5 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700"
              role="alert"
            >
              {shownProblems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          {saveError && (
            <p className="text-xs text-red-600" role="alert">
              {saveError}
            </p>
          )}
        </div>

        <aside className="w-full space-y-4 md:w-80 md:shrink-0">
          <SegmentedControl
            ariaLabel="Ways to draw the room"
            options={TABS}
            value={tab}
            onChange={changeTab}
            variant="chips"
          />
          <div>
            {tab === 'shape' && <PresetPanel editor={editor} />}
            {tab === 'walls' && <WallsPanel editor={editor} />}
            {tab === 'trace' && (
              <TracePanel
                editor={editor}
                drawing={drawing}
                mode={mode}
                setMode={setMode}
                tracePoints={tracePoints}
                setTracePoints={setTracePoints}
                calibrationPoints={calibrationPoints}
                setCalibrationPoints={setCalibrationPoints}
                onFinishTrace={finishTrace}
              />
            )}
            {tab === 'features' && <FeaturesPanel editor={editor} />}
            {tab === 'doors' && <OpeningsPanel editor={editor} />}
          </div>
          {mode === 'edit' && (
            <div className="border-t border-gray-200 pt-3">
              <SelectionPanel editor={editor} />
            </div>
          )}
        </aside>
      </div>
    </EntityModal>
  );
}
